/**
 * Step3Payment — final step. Stripe Elements PaymentElement.
 *
 * Flow:
 *   1. On mount: call /me to confirm session + fetch fee amount
 *   2. Call /step3/payment-intent to get client_secret
 *   3. Render <Elements stripe={...} options={{clientSecret}}>
 *      with our PaymentForm inside, which uses useStripe()/useElements()
 *      to confirm the payment.
 *   4. confirmPayment redirects to /portal/thanks via return_url. The Stripe
 *      webhook (separate path) writes the payment to the prospect via the
 *      record_prospect_a2p_payment RPC.
 */

import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { createPaymentIntent, getMe } from '../lib/api';

interface Props {
  navigate: (path: string) => void;
}

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!STRIPE_KEY) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(STRIPE_KEY);
  return stripePromise;
}

export default function Step3Payment({ navigate }: Props) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Confirm session + fetch fee + create intent
      const me = await getMe();
      if (cancelled) return;
      if (!me.success || !me.prospect) {
        navigate('/portal/login');
        return;
      }
      if (me.prospect.next_step === 'all_complete') {
        navigate('/portal/thanks');
        return;
      }

      const piRes = await createPaymentIntent();
      if (cancelled) return;
      if (!piRes.success || !piRes.client_secret) {
        setError(piRes.error || 'Payment is temporarily unavailable. Please try again shortly.');
        setLoading(false);
        return;
      }
      setClientSecret(piRes.client_secret);
      setAmountCents(piRes.amount_cents ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (!STRIPE_KEY) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 text-center max-w-md mx-auto">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Payment unavailable</h1>
        <p className="text-slate-600 dark:text-slate-300 text-sm">
          Payment isn't configured yet. Please contact support.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 text-center max-w-md mx-auto">
        <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-slate-600 dark:text-slate-300 text-sm">Preparing your payment…</p>
      </div>
    );
  }

  if (error || !clientSecret || !amountCents) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-8 text-center max-w-md mx-auto">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Hmm…</h1>
        <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
          {error || 'Payment is temporarily unavailable.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
        >
          Try again
        </button>
      </div>
    );
  }

  // Pick the Stripe Elements appearance to align with our dark/light mode
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium mb-1">
          Step 3 of 3
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white">
          Registration Fee
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mt-2">
          This one-time <strong>${(amountCents / 100).toFixed(2)}</strong> fee covers the
          registration we file with mobile carriers so you can start texting your contacts.
          Once registered, your account is ready to use.
        </p>
      </div>

      <Elements
        stripe={getStripe()}
        options={{
          clientSecret,
          appearance: { theme: isDark ? 'night' : 'stripe' },
        }}
      >
        <PaymentForm amountCents={amountCents} />
      </Elements>
    </div>
  );
}

// ============================================================================
// PaymentForm — uses Stripe hooks; must be inside <Elements>
// ============================================================================

function PaymentForm({ amountCents }: { amountCents: number }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/portal/thanks`,
      },
    });

    // confirmPayment will redirect on success. If we get here, there was an
    // immediate validation error (e.g. card declined, incomplete details).
    if (confirmError) {
      setError(confirmError.message || 'Payment could not be completed. Please check your card details and try again.');
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />

      {error && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
        You authorize a one-time charge of <strong>${(amountCents / 100).toFixed(2)}</strong> for
        A2P registration. This fee is non-refundable once the registration process begins.
      </p>

      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full px-4 py-3 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
      >
        {submitting ? 'Processing…' : `Pay $${(amountCents / 100).toFixed(2)}`}
      </button>
    </form>
  );
}
